import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api, reportApi } from '../../lib/api'
import { useAuth } from '../../core/AuthContext'
import { validateEmail } from '../../lib/utils'
import PhotoUploadMulti from '../../components/PhotoUploadMulti'
import LocationPicker from '../../components/LocationPicker'
import Icon from '../../components/Icon'
import Spinner from '../../components/Spinner'
import { compressImage } from '../../lib/utils'

const STEPS = [
  { n: 1, label: 'Photo Evidence' },
  { n: 2, label: 'Category' },
  { n: 3, label: 'Location' },
  { n: 4, label: 'Details & Send' },
]

const CATEGORIES = [
  { name: 'Pothole', icon: 'remove_road' },
  { name: 'Street Light', icon: 'light' },
  { name: 'Damaged Signage', icon: 'signpost' },
  { name: 'Broken Guardrail', icon: 'fence' },
  { name: 'Fly Tipping', icon: 'delete' },
  { name: 'Other', icon: 'more_horiz' },
]

export default function NewReportPage() {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [step, setStep] = useState(1)
  const [photos, setPhotos] = useState([])
  const [category, setCategory] = useState('')
  const [suggestedCategory, setSuggestedCategory] = useState('')
  const [location, setLocation] = useState('')
  const [description, setDescription] = useState('')
  const [geoLocation, setGeoLocation] = useState(null)
  const [error, setError] = useState('')

  // Guest email verification (skipped for signed-in users)
  const [guestEmail, setGuestEmail] = useState('')
  const [guestCode, setGuestCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [emailVerified, setEmailVerified] = useState(false)
  const [guestBusy, setGuestBusy] = useState(false)
  const [guestError, setGuestError] = useState('')

  const sendGuestCode = async () => {
    setGuestError('')
    if (!validateEmail(guestEmail)) {
      setGuestError('Please enter a valid email address')
      return
    }
    setGuestBusy(true)
    try {
      await api.post('/api/snagmatic/guest/request-code', { email: guestEmail })
      setCodeSent(true)
    } catch (err) {
      setGuestError(err.response?.data?.error || 'Could not send the code. Try again.')
    } finally {
      setGuestBusy(false)
    }
  }

  const verifyGuestEmail = async () => {
    setGuestError('')
    setGuestBusy(true)
    try {
      await api.post('/api/snagmatic/guest/verify-code', { email: guestEmail, code: guestCode })
      setEmailVerified(true)
    } catch (err) {
      setGuestError(err.response?.data?.error || 'Invalid or expired code.')
    } finally {
      setGuestBusy(false)
    }
  }

  const [guestResult, setGuestResult] = useState(null)

  const submitMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await reportApi.create(formData)
      return response.data
    },
    onSuccess: (data) => {
      if (user) {
        navigate(`/reports/${data.id}?success=true`)
      } else {
        setGuestResult(data)
      }
    },
    onError: (error) => {
      setError(error.response?.data?.error || 'Failed to submit report')
    },
  })

  const handlePhotosSelected = (photoList) => {
    setPhotos(photoList)
    setError('')
    // Auto-suggest category from first photo
    if (photoList.length > 0 && !category) {
      setSuggestedCategory('Pothole')
      setCategory('Pothole')
    }
  }

  const handleLocationConfirmed = ({ lat, lng, address }) => {
    setGeoLocation({ lat, lng })
    setLocation(address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`)
    setError('')
  }

  const handleSubmit = async () => {
    if (photos.length === 0 || !category || !location) {
      setError('Please fill in all required fields')
      return
    }
    if (!user && !emailVerified) {
      setError('Please verify your email before submitting')
      return
    }

    setError('')
    const formData = new FormData()

    // Append all photos (use annotated version if available)
    photos.forEach((photo, index) => {
      const photoToSubmit = photo.annotated || photo.dataUrl
      // Convert data URL to blob
      fetch(photoToSubmit)
        .then(res => res.blob())
        .then(blob => {
          formData.append(`photo_${index}`, new File([blob], `photo_${index}.jpg`, { type: 'image/jpeg' }))
        })
    })

    formData.append('category', category)
    formData.append('location', location)
    formData.append('description', description)
    formData.append('photoCount', photos.length)

    if (geoLocation) {
      formData.append('latitude', geoLocation.lat)
      formData.append('longitude', geoLocation.lng)
    }
    if (!user) {
      formData.append('email', guestEmail)
      formData.append('verificationCode', guestCode)
    }

    // Wait for all blobs to be appended
    setTimeout(() => submitMutation.mutate(formData), 100)
  }

  const canProceed =
    (step === 1 && photos.length > 0) ||
    (step === 2 && !!category) ||
    (step === 3 && !!location) ||
    step === 4

  if (submitMutation.isPending) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" label="Submitting your report..." />
      </div>
    )
  }

  // Guest success screen (guests can't open the tracked report page)
  if (guestResult) {
    return (
      <div className="px-4 md:px-10 py-6">
        <div className="max-w-md mx-auto text-center space-y-5 pt-12">
          <div className="inline-flex bg-status-resolved/15 border border-status-resolved/30 p-4 rounded-full">
            <Icon name="check_circle" filled className="text-status-resolved text-5xl" />
          </div>
          <h1 className="text-headline-lg-mobile md:text-headline-lg">Report submitted</h1>
          <p className="text-body-md text-text-secondary">
            Your report has been sent to <span className="text-text-primary font-semibold">{guestResult.sentTo}</span>.
            We've emailed a confirmation to <span className="text-text-primary">{guestEmail}</span> —
            status updates will land there too.
          </p>
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-label-sm text-text-muted uppercase tracking-wide mb-1">Reference</p>
            <p className="text-label-md font-mono text-text-primary break-all">{guestResult.id}</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 bg-accent text-surface-dark font-semibold text-label-md px-6 py-3 rounded-xl hover:bg-accent-light transition"
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 md:px-10 py-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Step header */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-label-md font-bold text-accent uppercase tracking-wide">
              Step {step} of 4
            </span>
            <span className="text-label-md text-text-secondary">{STEPS[step - 1].label}</span>
          </div>
          <div className="h-1.5 bg-surface-high rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
        </div>

        {error && (
          <div className="p-4 bg-status-urgent/10 border border-status-urgent rounded-xl">
            <p className="text-status-urgent text-label-md">{error}</p>
          </div>
        )}

        {/* Step 1: Photos */}
        {step === 1 && (
          <div className="space-y-5">
            <div className="space-y-2">
              <h1 className="text-headline-lg-mobile md:text-headline-lg">Document the issue</h1>
              <p className="text-body-md text-text-secondary">
                Take multiple photos from different angles. Click each photo to annotate or remove faces for privacy.
              </p>
            </div>

            <PhotoUploadMulti onPhotosSelected={handlePhotosSelected} maxPhotos={5} />

            <div className="flex items-start gap-3 bg-surface border border-border rounded-xl p-4">
              <Icon name="info" className="text-status-pending text-xl shrink-0 mt-0.5" />
              <p className="text-body-md text-text-secondary">
                Multiple photos help the team see the issue from different angles. You can annotate each photo individually.
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Category */}
        {step === 2 && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-headline-lg-mobile md:text-headline-lg">Identify Category</h1>
              {suggestedCategory && (
                <span className="bg-accent/15 border border-accent/30 text-accent text-label-sm font-bold uppercase tracking-wide px-3 py-1 rounded-full">
                  AI Suggested
                </span>
              )}
            </div>
            <p className="text-body-md text-text-secondary">
              {suggestedCategory
                ? `Based on your photo, we've identified this as a ${suggestedCategory.toLowerCase()}. Correct us if we're wrong.`
                : 'Select the category that best describes the issue.'}
            </p>

            <div className="flex flex-wrap gap-3">
              {CATEGORIES.map((cat) => {
                const selected = category === cat.name
                return (
                  <button
                    key={cat.name}
                    onClick={() => setCategory(cat.name)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl text-label-md transition min-h-0 ${
                      selected
                        ? 'bg-accent text-surface-dark font-semibold shadow-lg shadow-accent/20'
                        : 'bg-surface-high text-text-primary hover:bg-surface-bright'
                    }`}
                  >
                    <Icon name={cat.icon} className="text-xl" />
                    {cat.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Step 3: Location */}
        {step === 3 && (
          <div className="space-y-5">
            <h1 className="text-headline-lg-mobile md:text-headline-lg">Confirm Location</h1>

            {geoLocation && location ? (
              <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Icon name="check_circle" filled className="text-status-resolved text-2xl shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-label-md text-text-primary break-words">{location}</p>
                    <p className="text-label-sm text-text-muted font-mono">
                      {geoLocation.lat.toFixed(6)}, {geoLocation.lng.toFixed(6)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setGeoLocation(null)
                    setLocation('')
                  }}
                  className="text-accent text-label-md hover:underline min-h-0"
                >
                  Change location
                </button>
              </div>
            ) : (
              <>
                <LocationPicker onConfirm={handleLocationConfirmed} />

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-label-sm text-text-muted">or describe it</span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                <div>
                  <label className="text-label-sm text-text-secondary mb-1.5 block">
                    Address or landmark
                  </label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. 241 Baker Street, near the bus stop"
                    className="w-full"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 4: Details */}
        {step === 4 && (
          <div className="space-y-5">
            <h1 className="text-headline-lg-mobile md:text-headline-lg">
              Additional Details <span className="text-text-muted font-normal">(Optional)</span>
            </h1>

            <textarea
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue… (e.g. depth of pothole, near the bus stop)"
              className="w-full resize-y"
            />

            {/* Summary */}
            <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
              <SummaryRow icon="category" label="Category" value={category} />
              <SummaryRow icon="location_on" label="Location" value={location} />
              <SummaryRow icon="photo_camera" label="Photos" value={photos.length > 0 ? `${photos.length} attached` : 'Missing'} />
            </div>

            {/* Guest email verification — required just before submission */}
            {!user && (
              <div className="bg-surface border border-border rounded-xl p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-accent/15 border border-accent/20 p-2.5 rounded-xl shrink-0">
                    <Icon name="mark_email_read" className="text-accent text-xl" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-label-md text-text-primary">Verify your email to submit</h3>
                    <p className="text-body-md text-text-secondary">
                      This matters: it's how the responding organization can reach you if they need
                      more details, and where your status updates will be sent. No account needed.
                    </p>
                  </div>
                </div>

                {emailVerified ? (
                  <div className="flex items-center gap-2 bg-status-resolved/10 border border-status-resolved/40 rounded-xl p-3">
                    <Icon name="check_circle" filled className="text-status-resolved text-xl" />
                    <p className="text-label-md text-text-primary">
                      {guestEmail} verified — you're ready to submit.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={guestEmail}
                        onChange={(e) => {
                          setGuestEmail(e.target.value)
                          setCodeSent(false)
                          setGuestCode('')
                        }}
                        placeholder="you@example.com"
                        className="flex-1 min-w-0"
                        disabled={guestBusy}
                      />
                      <button
                        onClick={sendGuestCode}
                        disabled={guestBusy || !guestEmail}
                        className="shrink-0 bg-accent text-surface-dark font-semibold text-label-md px-4 rounded-xl hover:bg-accent-light transition disabled:opacity-40"
                      >
                        {guestBusy && !codeSent ? 'Sending…' : codeSent ? 'Resend' : 'Send code'}
                      </button>
                    </div>

                    {codeSent && (
                      <div className="space-y-2">
                        <p className="text-label-sm text-text-muted">
                          We emailed a 6-character code to {guestEmail}. Enter it below.
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={guestCode}
                            onChange={(e) => setGuestCode(e.target.value.toUpperCase().slice(0, 6))}
                            placeholder="ABC123"
                            maxLength={6}
                            className="flex-1 min-w-0 font-mono tracking-[0.4em] text-center uppercase"
                            disabled={guestBusy}
                          />
                          <button
                            onClick={verifyGuestEmail}
                            disabled={guestBusy || guestCode.length !== 6}
                            className="shrink-0 bg-accent text-surface-dark font-semibold text-label-md px-4 rounded-xl hover:bg-accent-light transition disabled:opacity-40"
                          >
                            {guestBusy ? 'Checking…' : 'Verify'}
                          </button>
                        </div>
                      </div>
                    )}

                    {guestError && (
                      <p className="text-status-urgent text-label-md">{guestError}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer actions */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={() => (step === 1 ? navigate(-1) : setStep(step - 1))}
            className="flex-1 border border-border text-text-primary text-label-md rounded-xl py-3.5 hover:bg-surface-high transition"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 4 ? (
            <button
              onClick={() => canProceed && setStep(step + 1)}
              disabled={!canProceed}
              className="flex-[2] flex items-center justify-center gap-2 bg-accent text-surface-dark font-semibold text-label-md rounded-xl py-3.5 shadow-lg shadow-accent/20 hover:bg-accent-light active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              Next Step
              <Icon name="arrow_forward" className="text-xl" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitMutation.isPending || (!user && !emailVerified)}
              className="flex-[2] flex items-center justify-center gap-2 bg-accent text-surface-dark font-semibold text-label-md rounded-xl py-3.5 shadow-lg shadow-accent/20 hover:bg-accent-light active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              <Icon name="send" className="text-xl" />
              Send Report
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon name={icon} className="text-accent text-xl" />
      <span className="text-label-sm text-text-muted w-20">{label}</span>
      <span className="text-body-md text-text-primary truncate">{value || '—'}</span>
    </div>
  )
}
